import React, { useState, useEffect, useRef } from 'react';
import {
  Award,
  FileText,
  CheckCircle,
  ArrowRight,
  Download,
  Users,
  Sparkles,
  School,
  User,
  Zap
} from 'lucide-react';
import FeatureStatusBadge from '../shared/FeatureStatusBadge';
import SectionControls from '../shared/SectionControls';

const ProfileToBragSheetFlow = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [isInView, setIsInView] = useState(false);
  const [profileDataVisible, setProfileDataVisible] = useState({});
  const [extractedDataList, setExtractedDataList] = useState([]);
  const [showExtractionComplete, setShowExtractionComplete] = useState(false);
  const [bragSheetLines, setBragSheetLines] = useState([]);
  const [teacherPanelVisible, setTeacherPanelVisible] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [bragSheetState, setBragSheetState] = useState('generating'); // 'generating', 'filling', 'complete'
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => startAnimatedSequence(), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const startAnimatedSequence = () => {
    // Reset all states
    setProfileDataVisible({});
    setExtractedDataList([]);
    setShowExtractionComplete(false);
    setBragSheetLines([]);
    setTeacherPanelVisible(false);
    setSelectedTeacher(null);
    setBragSheetState('generating');

    // Stage 1: Profile data animates in (0-500ms)
    const profileItems = ['academic', 'activities', 'achievements'];
    profileItems.forEach((item, index) => {
      setTimeout(() => {
        setProfileDataVisible(prev => ({ ...prev, [item]: true }));
      }, 150 * index);
    });

    // Stage 2: Data extraction list appears (500-2000ms)
    setTimeout(() => {
      const dataItems = [
        'GPA: 3.89',
        'SAT Score: 1520',
        '12 AP Courses',
        'Robotics Team Captain',
        'State Championship Winner',
        'Community Garden Founder',
        'Engineering Internship',
        'National Engineering Award'
      ];

      dataItems.forEach((item, index) => {
        setTimeout(() => {
          setExtractedDataList(prev => [...prev, item]);
        }, 150 * index);
      });

      // Show "All data extracted" at the end
      setTimeout(() => {
        setShowExtractionComplete(true);
      }, 150 * dataItems.length + 200);
    }, 1000);

    // PAUSE after data extraction completes - give user time to read (1000ms beat)

    // Stage 3: Brag sheet populates (3500ms start - delayed by 1500ms for beat)
    setTimeout(() => {
      setBragSheetState('filling');

      // Populate brag sheet content progressively
      const lines = ['academic', 'leadership', 'achievements'];
      lines.forEach((line, index) => {
        setTimeout(() => {
          setBragSheetLines(prev => [...prev, line]);
          // Set to complete state after last line with smoother transition
          if (index === lines.length - 1) {
            setTimeout(() => setBragSheetState('complete'), 800);
          }
        }, 500 * index);
      });
    }, 3500);

    // Stage 4: Teacher panel slides up over profile (5500ms - after brag sheet is complete)
    setTimeout(() => {
      setTeacherPanelVisible(true);
    }, 5000);

    // Stage 5: Cycle through teachers with 1000ms each (6000ms start)
    setTimeout(() => {
      // First teacher immediately
      setTimeout(() => setSelectedTeacher('mathTeacher'), 1000);
      // Second teacher after 1000ms
      setTimeout(() => setSelectedTeacher('englishTeacher'), 2000);
      // Third teacher after another 1000ms
      setTimeout(() => setSelectedTeacher('scienceTeacher'), 3000);

      // Mark animation as complete
      setTimeout(() => {
        onAnimationComplete && onAnimationComplete();
      }, 4000);
    }, 6000);
  };

  // Sample profile data
  const profileData = {
    academic: {
      gpa: '3.89',
      sat: '1520',
      coursework: '12 AP courses',
      rank: 'Top 5%'
    },
    activities: {
      robotics: 'Team Captain - State Champions',
      garden: 'Community Garden Founder',
      intern: 'Civil Engineering Internship'
    },
    achievements: {
      award1: 'National Engineering Award',
      science: 'Regional Science Fair Winner'
    }
  };

  return (
    <div ref={sectionRef} className="max-w-7xl mx-auto px-4 relative w-full">

      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-50 rounded-full">
            <Award className="w-4 h-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">
              Profile to Brag Sheet
            </span>
          </div>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          One Profile, Every Document
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          No one wants to type out the same activities list 47 times. Enter everything once, and it will be formatted for every form, brag sheet, and teacher packet you need.
        </p>
      </div>

      <div className="relative min-h-[600px] w-full">
        {/* Fixed-width grid to prevent layout shift */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr] gap-8 w-full">
          {/* Left Column - Profile with overlapping Teacher Panel */}
          <div className="relative w-full min-w-0">
            {/* Student Profile - Always visible */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden w-full">
              <div className="px-6 py-4 bg-gradient-to-r from-azure-500 to-indigo-500">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Student Profile
                </h3>
              </div>

              <div className="p-6 space-y-6">
                {/* Academic Section */}
                <div className={`space-y-3 transition-all duration-500 ${
                  profileDataVisible.academic ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                    <School className="w-4 h-4 text-slate-600" />
                    Academic Performance
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-600">GPA</div>
                      <div className="font-bold text-slate-900">{profileData.academic.gpa}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-600">SAT Score</div>
                      <div className="font-bold text-slate-900">{profileData.academic.sat}</div>
                    </div>
                  </div>
                </div>

                {/* Activities Section */}
                <div className={`space-y-3 transition-all duration-500 delay-150 ${
                  profileDataVisible.activities ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <h4 className="font-semibold text-slate-900">Key Activities</h4>
                  <div className="space-y-2">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="font-medium text-slate-900">{profileData.activities.robotics}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="font-medium text-slate-900">{profileData.activities.garden}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="font-medium text-slate-900">{profileData.activities.intern}</div>
                    </div>
                  </div>
                </div>

                {/* Achievements Section */}
                <div className={`space-y-3 transition-all duration-500 delay-300 ${
                  profileDataVisible.achievements ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}>
                  <h4 className="font-semibold text-slate-900">Achievements</h4>
                  <ul className="space-y-1 text-sm text-slate-700">
                    <li>• {profileData.achievements.award1}</li>
                    <li>• {profileData.achievements.science}</li>
                    <li>• {profileData.achievements.scholarship}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Teacher Customization Panel - Overlaps bottom of profile */}
            <div className={`absolute bottom-0 inset-x-0 bg-white rounded-xl shadow-2xl border-2 border-slate-900 overflow-hidden transition-all duration-500 ${
              teacherPanelVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
            }`}>
              <div className="px-6 py-4 bg-slate-900">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Customize for Each Teacher
                </h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-slate-600 mb-4">
                  The same data adapts to highlight what each teacher values most
                </p>

                {/* Teacher Options */}
                <div className="space-y-3">
                  <button
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-400 ${
                      selectedTeacher === 'mathTeacher'
                        ? 'border-magenta-500 bg-magenta-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-medium text-slate-900">Ms. Chen - AP Calculus</div>
                    <div className="text-xs text-slate-600 mt-1">Emphasize: Problem-solving, analytical thinking</div>
                  </button>

                  <button
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-400 ${
                      selectedTeacher === 'englishTeacher'
                        ? 'border-gold-400 bg-gold-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-medium text-slate-900">Mr. Rodriguez - English Lit</div>
                    <div className="text-xs text-slate-600 mt-1">Emphasize: Writing skills, creativity, communication</div>
                  </button>

                  <button
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-400 ${
                      selectedTeacher === 'scienceTeacher'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-medium text-slate-900">Dr. Patel - Physics</div>
                    <div className="text-xs text-slate-600 mt-1">Emphasize: Research, experimentation, innovation</div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Brag Sheet (Always Visible) */}
          <div className="relative w-full min-w-0">
            <div className={`bg-white rounded-xl shadow-lg overflow-hidden w-full transition-all duration-400 ${
              bragSheetState === 'generating' ? 'border-2 border-slate-300' :
              bragSheetState === 'filling' ? 'border-2 border-slate-600' :
              selectedTeacher === 'mathTeacher' ? 'border-2 border-magenta-500' :
              selectedTeacher === 'englishTeacher' ? 'border-2 border-gold-400' :
              selectedTeacher === 'scienceTeacher' ? 'border-2 border-teal-500' :
              'border-2 border-teal-500'
            }`}>
              <div className={`px-6 py-4 transition-all duration-400 ${
                bragSheetState === 'generating' ? 'bg-gradient-to-r from-slate-500 to-slate-600' :
                bragSheetState === 'filling' ? 'bg-slate-600' :
                selectedTeacher === 'mathTeacher' ? 'bg-magenta-500' :
                selectedTeacher === 'englishTeacher' ? 'bg-gold-500' :
                selectedTeacher === 'scienceTeacher' ? 'bg-teal-500' :
                'bg-slate-700'
              }`}>
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {bragSheetState === 'generating' && 'Brag Sheet Generating...'}
                  {bragSheetState !== 'generating' && selectedTeacher === 'mathTeacher' && 'Customized for Ms. Chen'}
                  {bragSheetState !== 'generating' && selectedTeacher === 'englishTeacher' && 'Customized for Mr. Rodriguez'}
                  {bragSheetState !== 'generating' && selectedTeacher === 'scienceTeacher' && 'Customized for Dr. Patel'}
                  {bragSheetState !== 'generating' && !selectedTeacher && 'Auto-Generated Brag Sheet'}
                </h3>
              </div>

              <div className="p-6 space-y-6 min-h-[400px]">
                {/* Data extraction list (Stage 2) */}
                {bragSheetState === 'generating' && (
                  <div className="space-y-2">
                    <div className="text-sm text-slate-600 mb-3">Extracting data from profile...</div>
                    <div className="space-y-1">
                      {extractedDataList.map((item, index) => (
                        <div
                          key={index}
                          className={`text-sm flex items-center gap-2 transition-all duration-400 ${
                            index === extractedDataList.length - 1
                              ? 'text-slate-900 font-medium'
                              : 'text-slate-400'
                          }`}
                        >
                          <CheckCircle className="w-3 h-3 text-emerald-500" />
                          {item}
                        </div>
                      ))}
                    </div>
                    {showExtractionComplete && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <div className="flex items-center gap-2 text-emerald-600 font-medium">
                          <CheckCircle className="w-4 h-4" />
                          All data extracted successfully
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Brag sheet content (Stage 3+) */}
                {bragSheetState !== 'generating' && (
                  <>
                    {/* Academic Excellence */}
                    <div className={`space-y-3 transition-all duration-500 ${
                      bragSheetLines.includes('academic')
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                    }`}>
                      <h4 className="font-bold text-slate-900">
                        {selectedTeacher === 'mathTeacher' && 'Classroom Moment'}
                        {selectedTeacher === 'englishTeacher' && 'Classroom Moment'}
                        {selectedTeacher === 'scienceTeacher' && 'Classroom Moment'}
                        {!selectedTeacher && 'Academic Excellence'}
                      </h4>
                      <div className="prose prose-sm text-slate-700">
                        <p>
                        {selectedTeacher === 'mathTeacher' && (
                          <>
                            During the optimization unit, I was questioning why we were maximizing area instead of cost-effectiveness in the fence problem. You suggested I research actual materials pricing and redesign the problem to reflect a real-world situation. It felt like actual engineering work than homework, and you ended up changing the whole unit to make it more practical.
                          </>
                        )}
                        {selectedTeacher === 'englishTeacher' && (
                          <>
In our Gatsby discussion, I kept thinking about how the Valley of Ashes connected to modern environmental justice issues. My essay analyzing how Fitzgerald's industrial wasteland parallels today's climate inequality ended up leading our class into a two-day long discussion.</>
                        )}
                        {selectedTeacher === 'scienceTeacher' && (
                          <>
                            When some of the class was struggling with rotational motion concepts, I brought up how we could think of it in terms of skateboarding. You said you'd never seen someone translate physics into "street language" so effectively, and it made me realize I love finding ways to make complex concepts accessible to different people.
                          </>
                        )}
                        {!selectedTeacher && (
                            <>
                              Sheppard maintains a <strong>3.89 GPA</strong> while taking the most rigorous course load,
                              including <strong>12 AP courses</strong>. Ranked in the <strong>top 5%</strong> of the class.
                            </>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Leadership & Impact */}
                    <div className={`space-y-3 transition-all duration-500 delay-400 ${
                      bragSheetLines.includes('leadership')
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                    }`}>
                      <h4 className="font-bold text-slate-900">Leadership & Community Impact</h4>
                      <ul className="space-y-2 text-sm text-slate-700">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                          <span><strong>Robotics Team Captain</strong> - Led team to State Championship</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                          <span><strong>Community Garden Founder</strong> - Created sustainable food source</span>
                        </li>
                      </ul>
                    </div>

                    {/* Notable Achievements */}
                    <div className={`space-y-3 transition-all duration-500 delay-800 ${
                      bragSheetLines.includes('achievements')
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                    }`}>
                      <h4 className="font-bold text-slate-900">Notable Achievements</h4>
                      <ul className="space-y-1 text-sm text-slate-700">
                        <li>• National Engineering Award Recipient</li>
                        <li>• Regional Science Fair Winner</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section Controls */}
      {selectedTeacher && (
        <SectionControls
          onReplay={startAnimatedSequence}
          onContinue={onContinue}
          isLastSection={isLastSection}
          showControls={true}
        />
      )}
    </div>
  );
};

export default ProfileToBragSheetFlow;